/* eslint-disable react/jsx-indent */
import i18n from 'i18next';
import { FC, useMemo } from 'react';
import { TranslateFn, useTranslate } from 'hooks/useTranslate';

import {
  IncentiveOffer,
  IncentiveOfferPlanClass,
  IncentiveOfferType,
  IncentiveOffersSummary,
} from 'modules/incentivesMarket/types';

import { formatCurrencyDecimals, formatNumber } from 'utils/formats';

import Button from 'modules/_shared/components/Buttons';
import { isOfferActive } from 'modules/incentivesMarket/utils/getOfferStatus';
import { useIncentivesMarket } from 'modules/incentivesMarket/contexts/IncentivesMarketContext';
import { isPlanClassVisible } from 'modules/incentivesMarket/utils/isPlanClassVisible';

type MarketSummaryCardProps = {
  type: IncentiveOfferType;
  title: string;
  summary: IncentiveOffersSummary;
  avgPrice: number;
  offers: IncentiveOffer[];
  actionLabel: string;
  actionVariant: 'info' | 'primary';
  viewAllLabel: string;
  onViewOffer: (offerId: string) => void; // eslint-disable-line no-unused-vars
  onViewAll: () => void;
};

const getBeneficiaryName = (offer: IncentiveOffer): string => {
  if (offer.isSellerNameHidden && offer.type === 'SELL') {
    return i18n.t('HiddenName');
  }
  if (typeof offer.beneficiary === 'string') return offer.beneficiary;
  return offer.beneficiary?.name || '';
};

const formatOfferDescription = (offer: IncentiveOffer, t: TranslateFn) => {
  const totalShares = formatNumber(offer.unitsCount.total);
  const unitClass = isPlanClassVisible()
    ? (offer.unitClass as IncentiveOfferPlanClass)?.name || '-'
    : '';
  const unitPrice = formatCurrencyDecimals(offer.unitPrice);
  const shareLabel = t('IncentivesMarketUnitAbbr').toLowerCase();
  const partnerName = getBeneficiaryName(offer);

  const sharesBlock = [totalShares, shareLabel, unitClass]
    .filter(Boolean)
    .join(' ');
  const priceBlock = [unitPrice, shareLabel].filter(Boolean).join('/');
  const partnerBlock = partnerName;

  return `${sharesBlock} | ${priceBlock} | ${partnerBlock}`.trim();
};

const formatOfferTotalAmount = (offer: IncentiveOffer, t: TranslateFn) => {
  const totalLabel = t('Total');
  const totalAmount = formatCurrencyDecimals(
    offer.unitsCount.total * offer.unitPrice
  );

  return `${totalLabel}: ${totalAmount}`;
};

const MarketSummaryCard: FC<MarketSummaryCardProps> = ({
  type,
  title,
  summary,
  avgPrice,
  offers,
  actionLabel,
  actionVariant,
  viewAllLabel,
  onViewOffer,
  onViewAll,
}) => {
  const { t } = useTranslate();

  const mainStyle = { SELL: 'info', BUY: 'primary' };

  const activeLabel =
    offers.length === 1
      ? t('OfferActive').toLowerCase()
      : t('OfferActives').toLowerCase();

  return (
    <div
      className={`card card-bordered h-100 border-top-${mainStyle[type]}`}
      style={{ borderTopWidth: `3px` }}
    >
      <div className="card-inner h-100 d-flex flex-column">
        <div className="nk-block-head-sm">
          <div className="nk-block-between flex-wrap">
            <h5 className="nk-block-title mb-0">{title}</h5>
            <span className={`badge badge-dim badge-${mainStyle[type]}`}>
              {offers.length} {activeLabel}
            </span>
          </div>
          <p className="text-soft mb-3 mt-1">{t('SortedByPriceLowToHigh')}</p>
        </div>

        <div className="d-flex flex-nowrap justify-content-between mb-3 pb-3">
          <div className="text-center">
            <div className="overline-title-alt sub-text">
              {t('IncentivesMarketTotalUnits')}
            </div>
            <div className="lead-text amount">
              {formatNumber(summary.unitsCount)}
            </div>
          </div>
          <div className="text-center">
            <div className="overline-title-alt sub-text">{t('AvgPrice')}</div>
            <div className="lead-text">
              <span className="amount">{formatCurrencyDecimals(avgPrice)}</span>
              <span className="text-soft">/{t('IncentivesMarketUnitAbbr').toLowerCase()}</span>
            </div>
          </div>
          <div className="text-center">
            <div className="overline-title-alt sub-text">
              {t('TotalVolume')}
            </div>
            <div
              className={`lead-text amount text-${
                mainStyle[type as IncentiveOfferType]
              }`}
            >
              {formatCurrencyDecimals(summary.totalAmount)}
            </div>
          </div>
        </div>

        <div className="mb-0 flex-grow-1">
          {[...offers]
            .sort((a, b) => a.unitPrice - b.unitPrice)
            .slice(0, 3)
            .map((offer, index) => (
              <div
                key={offer._id}
                className={`d-flex justify-content-between align-items-center py-2 border-bottom border-light ${
                  index === 0 ? 'border-top' : ''
                }`}
              >
                <div className="flex-grow-1">
                  <div className="fw-medium">
                    {formatOfferDescription(offer, t)}
                  </div>
                  <div className="text-soft fs-12px">
                    {formatOfferTotalAmount(offer, t)}
                  </div>
                </div>

                <div className="ml-3">
                  <Button
                    className={`btn btn-sm btn-${actionVariant}`}
                    onClick={() => onViewOffer(offer._id)}
                  >
                    {actionLabel}
                  </Button>
                </div>
              </div>
            ))}
        </div>

        <Button
          variant="link"
          onClick={onViewAll}
          className="link link-primary mt-auto pt-3 btn btn-link p-0 text-start"
          disabled={offers?.length === 0}
        >
          {viewAllLabel} <em className="icon ni ni-arrow-long-right ml-1" />
        </Button>
      </div>
    </div>
  );
};

const IncentivesMarketSummaryCards: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useIncentivesMarket();

  const avgSellPrice = useMemo(() => {
    if (data.sellOffersSummary.unitsCount === 0) return 0;
    return (
      data.sellOffersSummary.totalAmount / data.sellOffersSummary.unitsCount
    );
  }, [data.sellOffersSummary]);

  const avgBuyPrice = useMemo(() => {
    if (data.buyOffersSummary.unitsCount === 0) return 0;
    return (
      data.buyOffersSummary.totalAmount / data.buyOffersSummary.unitsCount
    );
  }, [data.buyOffersSummary]);

  const filterActiveOffers = (offers: IncentiveOffer[]) =>
    offers
      .filter((offer) => isOfferActive(offer))
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

  return (
    <div className="nk-block nk-block-spacing">
      <div className="row gy-gs">
        {/* Sell Offers Summary */}
        <div className="col-12 col-lg-6">
          <MarketSummaryCard
            type={IncentiveOfferType.SELL}
            title={t('SellOffers')}
            summary={data.sellOffersSummary}
            avgPrice={avgSellPrice}
            offers={filterActiveOffers(data.sellOffers)}
            actionLabel={t('Buy')}
            actionVariant="info"
            viewAllLabel={t('ViewAllSellOffers')}
            onViewOffer={actions.handleViewOfferDetail}
            onViewAll={actions.handleViewSellOffers}
          />
        </div>

        {/* Buy Offers Summary */}
        <div className="col-12 col-lg-6">
          <MarketSummaryCard
            type={IncentiveOfferType.BUY}
            title={t('BuyOffers')}
            summary={data.buyOffersSummary}
            avgPrice={avgBuyPrice}
            offers={filterActiveOffers(data.buyOffers)}
            actionLabel={t('Sell')}
            actionVariant="primary"
            viewAllLabel={t('ViewAllBuyOffers')}
            onViewOffer={actions.handleViewOfferDetail}
            onViewAll={actions.handleViewBuyOffers}
          />
        </div>
      </div>
    </div>
  );
};

export default IncentivesMarketSummaryCards;
